import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MapPin, Zap, Plug, Euro } from "lucide-react";
import BookingFlow from "@/components/BookingFlow";

const SpotDetail = () => {
  const { id } = useParams<{ id: string }>();

  const { data: spot, isLoading } = useQuery({
    queryKey: ["charging_spot", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("charging_spots")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Lädt…</p>
      </div>
    );
  }

  if (!spot) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Ladepunkt nicht gefunden.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-3xl py-8">
        <Link to="/search">
          <Button variant="ghost" size="sm" className="mb-6 gap-2">
            <ArrowLeft className="h-4 w-4" /> Zurück zur Suche
          </Button>
        </Link>

        <div className="space-y-6">
          {/* Title */}
          <div>
            <h1 className="text-2xl font-bold text-foreground">{spot.title}</h1>
            <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" /> {spot.address}
            </p>
          </div>

          {/* Mode badge */}
          {(spot as any).availability_mode && (
            <div className="flex">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${
                (spot as any).availability_mode === "instant"
                  ? "bg-primary/10 text-primary"
                  : (spot as any).availability_mode === "request"
                    ? "bg-amber-100 text-amber-800"
                    : "bg-muted text-muted-foreground"
              }`}>
                {(spot as any).availability_mode === "instant" && "⚡ Sofort verfügbar"}
                {(spot as any).availability_mode === "request" && "💬 Nur auf Anfrage"}
                {(spot as any).availability_mode === "reservable" && "📅 Reservierbar"}
              </span>
            </div>
          )}

          {/* Info cards */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-border bg-card p-4 text-center">
              <Plug className="mx-auto h-5 w-5 text-primary" />
              <p className="mt-2 text-sm font-medium">{spot.connector_type}</p>
              <p className="text-xs text-muted-foreground">Steckertyp</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-4 text-center">
              <Zap className="mx-auto h-5 w-5 text-primary" />
              <p className="mt-2 text-sm font-medium">{spot.power_kw} kW</p>
              <p className="text-xs text-muted-foreground">Ladeleistung</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-4 text-center">
              <Euro className="mx-auto h-5 w-5 text-primary" />
              <p className="mt-2 text-sm font-medium">{spot.price_per_hour.toFixed(2)} €</p>
              <p className="text-xs text-muted-foreground">pro Stunde</p>
            </div>
          </div>

          {/* Description */}
          {spot.description && (
            <div>
              <h2 className="text-base font-semibold text-foreground">Beschreibung</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{spot.description}</p>
            </div>
          )}

          {/* Rules */}
          {spot.rules && (
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <h2 className="text-base font-semibold text-foreground">Regeln</h2>
              <p className="mt-1 text-sm text-muted-foreground">{spot.rules}</p>
            </div>
          )}

          {/* Booking */}
          <BookingFlow chargingSpot={spot} />
        </div>
      </div>
    </div>
  );
};

export default SpotDetail;
